import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  TaskAlt as TaskAltIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import AddReminderDrawer from "components/Modals/AddReminderDrawer";
import useKothar from "context/useKothar";
import DisplayDate, { DisplayDateTime } from "components/DisplayDate";
import { formatDisplayDate } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";

const Reminders = () => {
  const canCreateReminders = hasPermission(P.REMINDERS.CREATE);
  const canUpdateReminders = hasPermission(P.REMINDERS.UPDATE);
  const canDeleteReminders = hasPermission(P.REMINDERS.DELETE);
  const [{ reminderList, isReminderLoading }, { refetchReminderList }] = useKothar();
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pending" | "completed"
  const [openEditDrawer, setOpenEditDrawer] = useState({ state: false, id: null });

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    if (!reminderList?.length) {
      refetchReminderList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsRead = useCallback(async (type, id) => {
    let notificationIds;
    let url;
    const reminderIds =
      type === "single" ? [id] : table.getSelectedRowModel().rows.map((row) => row.original.id);
    try {
      setLoading(true);
      await axios.put(`${API_URL}/client-reminders/mark-as-read`, { reminderIds });
      toast.success("Reminders marked as read");
      table.resetRowSelection();
      refetchReminderList(); // No params needed - uses current pagination
    } catch (error) {
      toast.error("Failed to mark reminders as read");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this reminder?")) {
      return;
    }

    const notificationIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
    let url;
    if (type === "single") {
      url = `/client-reminders/${id}`;
    } else {
      url = `/client-reminders/multiple`;
    }
    try {
      setLoading(true);
      await axios.delete(`${API_URL}${url}`, {
        data: type !== "single" ? { reminderIds: notificationIds } : undefined,
      });
      toast.success("Reminders deleted successfully");
      table.resetRowSelection();
      refetchReminderList(); // No params needed - uses current pagination
    } catch (error) {
      toast.error(error?.errorMessage || "Failed to delete reminders");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleComplete = useCallback(async (id, currentlyCompleted) => {
    try {
      await axios.patch(`${API_URL}/client-reminders/${id}/complete`, {
        completed: !currentlyCompleted,
      });
      toast.success(currentlyCompleted ? "Marked as pending" : "Marked as completed");
      refetchReminderList();
    } catch {
      toast.error("Failed to update reminder");
    }
  }, []);

  const handleRefresh = () => {
    refetchReminderList();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Title",
        size: 200,
        maxSize: 200,
        minSize: 200,
        Cell: ({ row }) => <div className="whitespace-normal">{row.original.title}</div>,
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        size: 150,
        Cell: ({ row }) => <DisplayDate value={row.original.dueDate} />,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        size: 70,
        Cell: ({ row }) => (
          <div className="whitespace-normal">
            {row.original.priority === "High" ? (
              <span className="text-red-500">High</span>
            ) : row.original.priority === "Medium" ? (
              <span className="text-yellow-500">Medium</span>
            ) : (
              <span className="text-green-500">Low</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "reason",
        header: "Reason",
        size: 200,
      },

      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 200,
        Cell: ({ row }) => <DisplayDateTime value={row.original?.createdAt} />,
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 150,
        Cell: ({ row }) => (
          <Box className="flex gap-2">
            {canUpdateReminders && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => setOpenEditDrawer({ state: true, id: row.original.id })}
                  disabled={loading}
                >
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {canUpdateReminders && (
              <Tooltip title={row.original.completed ? "Mark as pending" : "Mark as completed"}>
                <IconButton
                  onClick={() => handleToggleComplete(row.original.id, row.original.completed)}
                  disabled={loading}
                >
                  {row.original.completed ? (
                    <TaskAltIcon color="success" />
                  ) : (
                    <UncheckedIcon color="action" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {canUpdateReminders &&
              (!row.original.read ? (
                <Tooltip title="Mark as read">
                  <IconButton
                    onClick={() => handleMarkAsRead("single", row.original.id)}
                    disabled={loading}
                  >
                    <CheckCircleIcon color="primary" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Already read">
                  <IconButton disabled>
                    <CheckCircleIcon color="disabled" />
                  </IconButton>
                </Tooltip>
              ))}
            {canDeleteReminders && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete("single", row.original.id)}
                  disabled={loading}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [reminderList, canCreateReminders, canUpdateReminders, canDeleteReminders, loading]
  );

  const filteredReminders = useMemo(() => {
    const list = reminderList || [];
    if (statusFilter === "pending") return list.filter((r) => !r.completed);
    if (statusFilter === "completed") return list.filter((r) => r.completed);
    return list;
  }, [reminderList, statusFilter]);

  const table = useMaterialReactTable({
    columns,
    data: filteredReminders,
    rowNumberDisplayMode: "original",
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [20, 50, 100, 200, 400],
    },
    paginationDisplayMode: "pages",

    state: {
      isLoading: isReminderLoading,
      isSaved: isReminderLoading,
      showProgressBars: isReminderLoading,
    },
    enablePagination: true,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    enableStickyFooter: true,
    muiSkeletonProps: {
      animation: "wave",
    },
    getRowId: (row) => row.id,

    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 300px)", // Adjust this value based on your header height
        minHeight: "calc(100vh - 300px)", // Set a minimum height
        overflowY: "auto",
        position: "relative", // Important for sticky positioning
      },
    },

    enableExpanding: true,
    renderDetailPanel: ({ row }) => {
      const r = row.original;
      return (
        <Box sx={{ p: 2, backgroundColor: "background.default" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} flexWrap="wrap">
            {r.clientName && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  CLIENT
                </Typography>
                <Typography variant="body2">{r.clientName}</Typography>
              </Box>
            )}
            {r.type && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  TYPE
                </Typography>
                <Typography variant="body2">{r.type}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                STATUS
              </Typography>
              <Typography variant="body2">
                {r.status || (r.completed ? "Completed" : "Pending")}
              </Typography>
            </Box>
          </Stack>

          {r.reason && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                REASON
              </Typography>
              <Typography variant="body2">{r.reason}</Typography>
            </>
          )}

          {r.description && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                DESCRIPTION
              </Typography>
              <Typography variant="body2">{r.description}</Typography>
            </>
          )}

          {r.reminderDates?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                SCHEDULED DATES
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mt={0.5}>
                {r.reminderDates.map((d) => (
                  <Chip key={d} size="small" label={formatDisplayDate(d)} variant="outlined" />
                ))}
              </Stack>
            </>
          )}

          {r.repeat?.enabled && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                REPEAT
              </Typography>
              <Typography variant="body2">
                Every {r.repeat.interval} {r.repeat.unit}
              </Typography>
            </>
          )}

          {(r.notifications?.clientMessage ||
            r.notifications?.adminMessage ||
            r.notifications?.channels?.length > 0) && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                NOTIFICATIONS
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={0.5}>
                {r.notifications?.channels?.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Channels
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.5}>
                      {r.notifications.channels.map((c) => (
                        <Chip key={c} size="small" label={c} />
                      ))}
                    </Stack>
                  </Box>
                )}
                {r.notifications?.clientMessage && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Client message
                    </Typography>
                    <Typography variant="body2">{r.notifications.clientMessage}</Typography>
                  </Box>
                )}
                {r.notifications?.adminMessage && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Admin message
                    </Typography>
                    <Typography variant="body2">{r.notifications.adminMessage}</Typography>
                  </Box>
                )}
              </Stack>
            </>
          )}

          {r.escalation?.enabled && r.escalation?.levels?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                ESCALATION
              </Typography>
              <Stack spacing={0.5} mt={0.5}>
                {r.escalation.levels.map((lvl, i) => (
                  <Typography key={i} variant="body2">
                    Level {i + 1}: Notify {lvl.notify} after {lvl.afterDays} day
                    {lvl.afterDays !== 1 ? "s" : ""}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
        </Box>
      );
    },
    positionToolbarAlertBanner: "bottom",
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "action.hover",
        },
        backgroundColor: !row.original.read ? "action.selected" : "background.paper",
      },
    }),
    renderTopToolbarCustomActions: () => (
      <div className="flex gap-2 justify-start items-center ml-4 flex-wrap">
        {canUpdateReminders && (
          <Button
            variant="outlined"
            startIcon={<CheckCircleIcon />}
            onClick={() => handleMarkAsRead("multiple")}
            disabled={!table.getSelectedRowModel().rows.length || loading}
          >
            Mark as Read
          </Button>
        )}
        {canDeleteReminders && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleDelete()}
            disabled={!table.getSelectedRowModel().rows.length || loading}
          >
            Delete
          </Button>
        )}
        <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
          {[
            { value: "all", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "completed", label: "Completed" },
          ].map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              clickable
              color={statusFilter === opt.value ? "primary" : "default"}
              variant={statusFilter === opt.value ? "filled" : "outlined"}
              onClick={() => setStatusFilter(opt.value)}
            />
          ))}
        </Stack>
      </div>
    ),
  });

  return (
    <div className="flex flex-col h-screen">
      <AddReminderDrawer
        open={openEditDrawer.state && (openEditDrawer.id ? canUpdateReminders : canCreateReminders)}
        id={openEditDrawer.id}
        handleClose={() => setOpenEditDrawer({ state: false, id: null })}
        onSuccess={() => {
          setOpenEditDrawer({ state: false, id: null });
          refetchReminderList();
        }}
      />
      <div className="w-full px-4 py-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text h-full"
          }
        >
          <Box className="flex justify-between items-center mb-0 p-4">
            <Typography variant="h5" className="font-bold">
              Reminders ({filteredReminders.length}
              {statusFilter !== "all" ? ` / ${reminderList?.length}` : ""}){" "}
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Typography>
            {canCreateReminders && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenEditDrawer({ state: true, id: null })}
              >
                Add reminder
              </Button>
            )}
          </Box>

          <div className="">
            {/* {loading ? (
              <Box className="flex justify-center items-center h-64">
                <Typography>Loading...</Typography>
              </Box>
            ) : ( */}
            <MaterialReactTable table={table} />
            {/* )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reminders;
